import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TablePagination,
  Skeleton,
} from "@mui/material";
import {
  descriptionCellStyle,
  likesViewsDateCommentsCellStyle,
  videoCellStyle,
  videoListTableStyle,
} from "./css";
import {
  extractTimeFromDateTime,
  extractTimeFromDateTime24,
  getReadableDateString,
} from "../../utils/commons";

import { getPlayList } from "../../services/ContentServices";
import { PlaylistThumbCard } from "../../components/ContentCard";
import { Video } from "./types";

export interface PlayList {
  id: string;
  name: string;
  description: string;
  thumbnailUrl: string;
  videos: Video[];
  createdAt: string;
  updatedAt: string;
  availableForVisitors: boolean;
}

const rowsPerPageOptions = [5, 10, 25];
const PLAYLIST_ITEM_HEIGHT = 60;
const PLAYLIST_THUMB_WIDTH = Math.round((PLAYLIST_ITEM_HEIGHT * 16) / 9);

interface PlayListTableProps {
  drawerOpen: boolean;
  setShowViewPlayList: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedPlayList: React.Dispatch<React.SetStateAction<string>>;
}
const PlayList: React.FC<PlayListTableProps> = ({
  drawerOpen,
  setShowViewPlayList,
  setSelectedPlayList,
}) => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
  const [playList, setPlayList] = useState<PlayList[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const compactVideoCellStyle = {
    ...videoCellStyle,
    width: PLAYLIST_THUMB_WIDTH,
    maxWidth: PLAYLIST_THUMB_WIDTH,
    minWidth: PLAYLIST_THUMB_WIDTH,
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchPlaylist = async () => {
    try {
      setLoading(true);
      const response = await getPlayList({ page, size: rowsPerPage });
      setPlayList(response.data.content);
      setTotalElements(response.data.totalElements);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaylist();
  }, [rowsPerPage, page, drawerOpen]);

  return (
    <>
      <Box sx={videoListTableStyle}>
        <TableContainer sx={{ height: 80 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={compactVideoCellStyle}>Playlists</TableCell>
                <TableCell sx={descriptionCellStyle}></TableCell>
                <TableCell sx={likesViewsDateCommentsCellStyle}>
                  Access
                </TableCell>
                <TableCell sx={likesViewsDateCommentsCellStyle}>
                  Last Updated
                </TableCell>
                <TableCell sx={likesViewsDateCommentsCellStyle}>
                  No of Videos
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
        <Box
          sx={{
            height: "100%",
            overflowY: "auto",
            scrollbarWidth: "none",
          }}
        >
          {" "}
          <Box>
            <TableContainer>
              <Table>
                <TableBody>
                  {loading &&
                    Array.from({ length: rowsPerPage }).map((_, index) => (
                      <PlayListTableRowSkeleton key={index} />
                    ))}

                  {!loading && playList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No data found
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading &&
                    playList.map((playlist) => (
                      <TableRow
                        key={playlist.id}
                        onClick={() => {
                          setShowViewPlayList(true);
                          setSelectedPlayList(playlist.id);
                        }}
                      >
                        <TableCell
                          sx={{
                            ...compactVideoCellStyle,
                            cursor: "pointer",
                            py: 0.5,
                          }}
                        >
                          <PlaylistThumbCard
                            srcUrl={playlist.thumbnailUrl}
                            count={playlist.videos.length}
                            width={"100%"}
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            ...descriptionCellStyle,
                            cursor: "pointer",
                            py: 0.5,
                          }}
                        >
                          <Box>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {playlist.name}
                            </p>
                            <p
                              style={{
                                wordBreak: "break-word",
                                margin: "4px 0 0",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {playlist.description.length > 150
                                ? `${playlist.description.slice(0, 150)}...`
                                : playlist.description}
                            </p>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}
                        >
                          {playlist?.availableForVisitors ? "Free" : "Paid"}
                        </TableCell>

                        <TableCell
                          sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}
                        >
                          {getReadableDateString(playlist.updatedAt as string)}
                          <br />
                          {extractTimeFromDateTime24(
                            playlist?.updatedAt as string
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}
                        >
                          {playlist.videos.length}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={totalElements}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};

export default PlayList;

const PlayListTableRowSkeleton = () => (
  <TableRow>
    <TableCell
      sx={{
        ...videoCellStyle,
        width: PLAYLIST_THUMB_WIDTH,
        maxWidth: PLAYLIST_THUMB_WIDTH,
        minWidth: PLAYLIST_THUMB_WIDTH,
        py: 0.5,
      }}
    >
      <Skeleton
        variant="rectangular"
        width={"100%"}
        height={PLAYLIST_ITEM_HEIGHT}
      />
    </TableCell>
    <TableCell sx={{ ...descriptionCellStyle, py: 0.5 }}>
      <Box>
        <Skeleton variant="text" width={150} height={20} />
        <Skeleton variant="text" width={300} height={40} />
      </Box>
    </TableCell>
    <TableCell sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}>
      <Skeleton variant="text" width={50} height={20} />
    </TableCell>
    <TableCell sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}>
      <Skeleton variant="text" width={60} height={20} />
    </TableCell>
    <TableCell sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}>
      <Skeleton variant="text" width={50} height={20} />
    </TableCell>
  </TableRow>
);
